import DOMPurify from 'dompurify';
import type { BlockNoteEditor } from '@blocknote/core';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Node } from '@tiptap/pm/model';
import { inlineContentToText, normalizeBlockId } from '@/ui/components/editor/utils';

export type DiffToken = {
  kind: 'same' | 'del' | 'ins';
  text: string;
};

const tokenize = (text: string) => text.split(/\s+/).filter(Boolean);

export const diffWords = (oldText: string, newText: string): DiffToken[] => {
  const a = tokenize(oldText);
  const b = tokenize(newText);
  const m = a.length;
  const n = b.length;

  const lcs: Uint32Array[] = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) {
    const row = lcs[i]!;
    const nextRow = lcs[i + 1]!;
    for (let j = n - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? nextRow[j + 1]! + 1 : Math.max(nextRow[j]!, row[j + 1]!);
    }
  }

  const tokens: DiffToken[] = [];
  const push = (kind: DiffToken['kind'], text: string) => {
    const last = tokens[tokens.length - 1];
    if (last?.kind === kind) {
      last.text += ` ${text}`;
      return;
    }
    tokens.push({ kind, text });
  };

  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    const wordA = a[i]!;
    const wordB = b[j]!;
    if (wordA === wordB) {
      push('same', wordA);
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      push('del', wordA);
      i++;
    } else {
      push('ins', wordB);
      j++;
    }
  }
  while (i < m) push('del', a[i++]!);
  while (j < n) push('ins', b[j++]!);
  return tokens;
};

export const findBlockContainers = (doc: Node, blockIds: Set<string>) => {
  const found = new Map<string, { pos: number; nodeSize: number }>();
  if (!blockIds.size) return found;
  doc.descendants((node, pos) => {
    if (node.type.name === 'blockContainer') {
      const id = node.attrs?.id;
      if (id && blockIds.has(id) && !found.has(id)) found.set(id, { pos, nodeSize: node.nodeSize });
    }
    return found.size < blockIds.size;
  });
  return found;
};

export const blocksToHTML = (editor: BlockNoteEditor<any, any, any>, blocks: any[]) =>
  DOMPurify.sanitize(editor.blocksToHTMLLossy(blocks));

const createPreviewWidget = (html: string) => {
  const wrapper = document.createElement('div');
  wrapper.className = 'bm-preview';
  wrapper.contentEditable = 'false';
  wrapper.innerHTML = html;
  return wrapper;
};

const createDiffWidget = ({
  diff,
  block,
  addedHTML = [],
}: {
  diff?: DiffToken[];
  block?: { type?: string; props?: { level?: number } };
  addedHTML?: string[];
}) => {
  const wrapper = document.createElement('div');
  wrapper.className = 'bm-diff';
  wrapper.contentEditable = 'false';
  if (diff?.length) {
    const redline = document.createElement('div');
    redline.className = 'bm-diff-redline';
    const el = document.createElement(
      block?.type === 'heading' ? `h${block.props?.level ?? 3}` : 'p',
    );
    for (const token of diff) {
      const tag = token.kind === 'del' ? 'del' : token.kind === 'ins' ? 'ins' : 'span';
      const span = document.createElement(tag);
      span.textContent = token.text;
      el.appendChild(span);
      el.appendChild(document.createTextNode(' '));
    }
    redline.appendChild(el);
    wrapper.appendChild(redline);
  }
  for (const html of addedHTML) {
    const panel = document.createElement('div');
    panel.className = 'bm-diff-added';
    panel.innerHTML = html;
    wrapper.appendChild(panel);
  }
  return wrapper;
};

export type ProposalOperationPreview = {
  id: string;
  type: 'update' | 'remove' | 'add';
  position?: 'before' | 'after';
  widget?: HTMLElement;
  redline?: boolean;
};

export type ProposalPreview = {
  operations: ProposalOperationPreview[];
  controls: HTMLElement;
};

export const buildOperationPreview = (
  editor: BlockNoteEditor<any, any, any>,
  operation: {
    id: string;
    type: 'update' | 'remove' | 'add';
    position?: 'before' | 'after';
    block?: any;
  },
): ProposalOperationPreview => {
  const id = normalizeBlockId(operation.id);
  if (operation.type === 'remove') return { id, type: 'remove' };
  if (operation.type === 'add')
    return {
      id,
      type: 'add',
      position: operation.position,
      widget: createPreviewWidget(blocksToHTML(editor, [operation.block])),
    };
  const original = editor.getBlock(id);
  const block = {
    ...original,
    ...operation.block,
    props: { ...original?.props, ...operation.block?.props },
  };
  if (original?.type === 'table' || block.type === 'table')
    return { id, type: 'update', widget: createPreviewWidget(blocksToHTML(editor, [block])) };
  return {
    id,
    type: 'update',
    redline: true,
    widget: createDiffWidget({
      diff: diffWords(
        inlineContentToText(original?.content),
        inlineContentToText(operation.block?.content),
      ),
      block,
    }),
  };
};

const previewKey = new PluginKey<{
  previews: Map<string, ProposalPreview>;
  decorations: DecorationSet;
}>('proposal-preview');

const buildDecorations = (doc: Node, previews: Map<string, ProposalPreview>) => {
  if (!previews.size) return DecorationSet.empty;
  const wanted = new Set<string>();
  for (const preview of previews.values())
    for (const operation of preview.operations) wanted.add(operation.id);
  const blocks = findBlockContainers(doc, wanted);
  const decorations: Decoration[] = [];
  let side = 0;
  for (const [previewId, preview] of previews) {
    let controlsPosition = doc.content.size - 1;
    for (const [index, operation] of preview.operations.entries()) {
      const block = blocks.get(operation.id);
      const before = operation.type === 'add' && operation.position === 'before';
      const position = block ? block.pos + (before ? 0 : block.nodeSize) : doc.content.size - 1;
      if (block) {
        const classes = ['bm-suggestion'];
        if (operation.redline) classes.push('bm-diff-source');
        else if (operation.type !== 'add') classes.push('bm-strike');
        decorations.push(
          Decoration.node(block.pos, block.pos + block.nodeSize, {
            class: classes.join(' '),
            'data-source': 'chat',
            'data-active': '',
          }),
        );
      }
      if (operation.widget)
        decorations.push(
          Decoration.widget(position, operation.widget, {
            key: `${previewId}:${index}`,
            side: ++side,
            stopEvent: () => true,
            ignoreSelection: true,
          }),
        );
      controlsPosition = position;
    }
    decorations.push(
      Decoration.widget(controlsPosition, preview.controls, {
        key: `${previewId}:controls`,
        side: ++side,
        stopEvent: () => true,
        ignoreSelection: true,
      }),
    );
  }
  return DecorationSet.create(doc, decorations);
};

export const proposalPreviewExtension = Extension.create({
  name: 'proposalPreview',
  addProseMirrorPlugins: () => [
    new Plugin({
      key: previewKey,
      state: {
        init: () => ({
          previews: new Map<string, ProposalPreview>(),
          decorations: DecorationSet.empty,
        }),
        apply: (transaction, previous) => {
          const change = transaction.getMeta(previewKey) as
            | { id: string; preview: ProposalPreview | null }
            | undefined;
          if (!change && !transaction.docChanged) return previous;
          const previews = new Map(previous.previews);
          if (change?.preview) previews.set(change.id, change.preview);
          else if (change) previews.delete(change.id);
          return { previews, decorations: buildDecorations(transaction.doc, previews) };
        },
      },
      props: {
        decorations: (state) => previewKey.getState(state)?.decorations,
      },
    }),
  ],
});

export const setProposalPreview = (view: EditorView, id: string, preview: ProposalPreview | null) =>
  view.dispatch(view.state.tr.setMeta(previewKey, { id, preview }));
